import Calendar from '@/components/calendar/Calendar';
import Avatar from '@/components/common/Avatar';
import { ButtonCusWithIcon } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import AvartarChange from '@/assets//images/change_photo.svg';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import { DATE_YMD_2, DEFAULT_IMAGE_LIMIT_SIZE, EMAIL_VALIDATE_REGEX, MAX_LENGTH_NUMBER, NUMBER_FULL_HALF_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { getAvatarFallbackName, isAdmin, isRoleEndUser } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { convertFullToHalf } from '@/utils/formatNumber';
import PasswordItem from './components/password/PasswordItem';
import CouponForeverItem from './components/coupon/CouponForeverItem';

export default observer(function MyAccountEk() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        authStore: { profileEk },
        generalStore: {
            myAccount, getMyAccount, editMyAccount, licenseTypeEk, getLicenseEk
        },
        fileStore: { uploadFile },
        modalStore: { showAlertModal }
    } = useStore();

    // form
    const validateSchema = yup.object().shape({
        avartarImageId: yup
            .number(),
        email: yup
            .string()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email'),
        phone: yup
            .string()
            .required('form_error_validate.required')
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        furiganaName: yup
            .string(),
        licenseType: yup
            .string()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.string().required('form_error_validate.required')
            }),
        licenseBeforeImageId: yup
            .number()
            .when(['roleId'], {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        licenseAfterImageId: yup
            .number()
            .when(['roleId'], {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        fullname: yup
            .string()
            .required('form_error_validate.required'),
        birthOfDate: yup
            .date()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.date().required('form_error_validate.required'),
                otherwise: _ => yup.date().nullable()
            }),
        zipCode: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        address: yup
            .string(),
        roleId: yup
            .number()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            avartarImageId: undefined,
            email: '',
            phone: '',
            furiganaName: '',
            licenseBeforeImageId: undefined,
            licenseAfterImageId: undefined,
            licenseType: '',
            fullname: '',
            birthOfDate: undefined,
            zipCode: '',
            address: '',
            roleId: undefined
        }
    });

    // state
    const [avartarImgUrl, setAvartarImgUrl] = useState<string>('');
    const [beforeImgUrl, setBeforeImgUrl] = useState<string>('');
    const [afterImgUrl, setAfterImgUrl] = useState<string>('');

    // watch form
    const watchRoleId = form.watch('roleId');

    // lifecycle
    useEffect(() => {
        fetchDataLicense();
        fetchDataAccount();
    }, []);

    // function
    const fetchDataLicense = async () => {
        await flowResult(getLicenseEk());
    };

    const fetchDataAccount = async () => {
        const res = await flowResult(getMyAccount());
        if (res) {
            const {
                avartarImage,
                email,
                phone,
                furiganaName,
                licenseBeforeImage,
                licenseAfterImage,
                licenseType,
                fullname,
                birthOfDate,
                zipCode,
                address,
                role
            } = res.data;

            if (avartarImage?.path) {
                setAvartarImgUrl(avartarImage.path);
            }

            if (licenseBeforeImage?.path) {
                setBeforeImgUrl(licenseBeforeImage?.path);
            }

            if (licenseAfterImage?.path) {
                setAfterImgUrl(licenseAfterImage?.path);
            }
            form.reset({
                avartarImageId: avartarImage?.id || undefined,
                email: email || '',
                phone: phone ? convertFullToHalf(phone) : '',
                furiganaName: furiganaName || '',
                licenseBeforeImageId: licenseBeforeImage?.id ? licenseBeforeImage?.id : undefined,
                licenseAfterImageId: licenseAfterImage?.id ? licenseAfterImage?.id : undefined,
                licenseType: (Array.isArray(licenseType) && licenseType?.length > 0) ?
                    licenseType[0] :
                    '',
                fullname: fullname || '',
                birthOfDate: birthOfDate ? new Date(birthOfDate) : undefined,
                zipCode: zipCode ? convertFullToHalf(zipCode) : '',
                address: address || '',
                roleId: role?.id || undefined
            });
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const fileType = file.type;
            if (!fileType.includes('image')) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: t('messages.only_upload_image')
                });
                e.target.value = '';
                return;
            }

            const maxSizeBytes = DEFAULT_IMAGE_LIMIT_SIZE * 1024 * 1024;
            if (file.size > maxSizeBytes) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: t('messages.exceed_size_limit', { size: DEFAULT_IMAGE_LIMIT_SIZE })
                });
                e.target.value = '';
                return;
            }

            const res = await flowResult(
                uploadFile({ files: file })
            );

            if (res?.ok) {
                const newImagePreview = URL.createObjectURL(file);
                setAvartarImgUrl(newImagePreview);
                if (res.data?.length) {
                    form.setValue('avartarImageId', res.data[0].id);
                }
            }
        }

        e.target.value = '';
    };

    const onSubmit = async (data: FormData) => {
        const {
            avartarImageId,
            email,
            phone,
            furiganaName,
            fullname,
            birthOfDate,
            zipCode,
            address,
            licenseType,
            licenseBeforeImageId,
            licenseAfterImageId
        } = data;
        const dataSubmit = {
            avartarImageId,
            email,
            phone: phone ? convertFullToHalf(phone) : '',
            furiganaName,
            fullname,
            birthOfDate: formatDateTime((birthOfDate || ''), DATE_YMD_2),
            zipCode: zipCode ? convertFullToHalf(zipCode) : '',
            address,
            licenseType: licenseType && !isNaN(Number(licenseType)) ? [Number(licenseType)] : [],
            licenseBeforeImageId: licenseBeforeImageId || null,
            licenseAfterImageId: licenseAfterImageId || null
        };
        const res = await flowResult(editMyAccount(dataSubmit));
        if (res) {
            getMyAccount();
            showAlertModal({
                id: 'edit-my-account-success',
                type: 'success',
                content: t('messages.update_success')
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='mt-0'>
                <div className='layout-card'>
                    {/* First info */}
                    <div className='mb-6'>

                        <div className='flex gap-6 justify-between mb-6'>
                            <h2 className='title-2 whitespace-nowrap'>{t('words_title.basic_info')}</h2>
                            <PasswordItem />
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>

                            <div className='col-span-1 md:col-span-1 min-h-40'>
                                <div className='flex flex-col justify-center items-center'>
                                    <FormField
                                        control={form.control}
                                        name='avartarImageId'
                                        render={() => (
                                            <FormItem>
                                                <Label htmlFor='avartar-image' className='relative cursor-pointer'>
                                                    <Avatar
                                                        src={avartarImgUrl}
                                                        fallback={getAvatarFallbackName(profileEk?.fullname)}
                                                        className='h-28 w-28 rounded-full border border-primary text-4xl'
                                                    />
                                                    <div className='absolute bottom-0 right-2'>
                                                        <img src={AvartarChange} alt='camera-icon' />
                                                    </div>
                                                </Label>
                                                <Input
                                                    type='file'
                                                    accept='image/*'
                                                    id='avartar-image'
                                                    onChange={handleImageChange}
                                                    className='hidden'
                                                />
                                            </FormItem>
                                        )}
                                    />
                                    <div className='mt-3 font-semibold text-base'>{profileEk?.fullname}</div>
                                </div>
                            </div>

                            <div className='col-span-1 md:col-span-2 grid gap-4'>
                                <div>
                                    <Label className='mb-2'>{t('field.email')}</Label>
                                    <div>{myAccount?.email || '--'}</div>
                                </div>
                            </div>

                        </div>

                    </div>

                    {/* Second info */}
                    <div className='grid gap-4'>
                        <FormField
                            control={form.control}
                            name='licenseType'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel required={!!watchRoleId && !isAdmin(Number(watchRoleId))}>
                                        {t('field.type_of_document')}
                                    </FormLabel>
                                    <FormControl>
                                        <Combobox
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.code);
                                                form.setValue('licenseType', item?.code);
                                            }}
                                            items={
                                                licenseTypeEk.map(item => ({
                                                    label: item.name,
                                                    value: item.id?.toString(),
                                                    code: item.id
                                                }))
                                            }
                                            search={false}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div>
                            <Label
                                required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                className='mb-2'
                            >
                                {t('field.card_image')}
                            </Label>
                            <div className='grid gap-4 grid-cols-1 sm:grid-cols-2'>
                                <div className='col-span-1'>
                                    <ImageUploadAccount
                                        form={form}
                                        filedName='licenseBeforeImageId'
                                        caption={t('words_title.before')}
                                        urlImagePreview={beforeImgUrl}
                                        isOcrNoToken={true}
                                        required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                    />
                                </div>
                                <div className='col-span-1'>
                                    <ImageUploadAccount
                                        filedName='licenseAfterImageId'
                                        form={form}
                                        caption={t('words_title.after')}
                                        urlImagePreview={afterImgUrl}
                                        required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='fullname'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.name_or_company_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='furiganaName'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.furigana_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='birthOfDate'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel
                                            required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                        >
                                            {t('field.date_of_birth')}
                                        </FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required={false}
                                                isInvalid={!!fieldState.error}
                                                disabled={[
                                                    {
                                                        after: new Date()
                                                    }
                                                ]}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='phone'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.phone_number')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                maxLength={MAX_LENGTH_NUMBER}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='zipCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.post_code')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.address')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {isRoleEndUser() && (
                                <div className='col-span-full'>
                                    <CouponForeverItem />
                                </div>
                            )}
                        </div>

                    </div>

                </div>

                <div className='mt-8 flex justify-center gap-4'>
                    <ButtonCusWithIcon
                        type='submit'
                        variant='default'
                        size='sm'
                        iconId='#icon-update'
                    >
                        {t('button.update')}
                    </ButtonCusWithIcon>
                </div>

            </form>
        </Form>
    );
});
